import { inject, Injectable } from '@angular/core';
import { SearchFetchService } from '@mode/shared/data-access-algolia';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, delay, map, of, switchMap, withLatestFrom, filter } from 'rxjs';
import * as AlgoliaSearchActions from './algolia-search.actions';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { Action } from '@ngrx/store';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';

@Injectable()
export class AlgoliaSearchEffects implements OnInitEffects {
  private actions$ = inject(Actions);

  constructor(
    private searchFetchService: SearchFetchService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private metaTagsService: MetaTagsService
  ) {}

  ngrxOnInitEffects(): Action {
    return AlgoliaSearchActions.loadAlgoliaSearchSettings();
  }

  settings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlgoliaSearchActions.loadAlgoliaSearchSettings),
      filter(() => !!this.metaTagsService.getTag(MetaTags.CurrentUsername)),
      switchMap(() =>
        this.searchFetchService.getSearchSettings().pipe(
          map(({ settings }) => AlgoliaSearchActions.loadAlgoliaSearchSettingsSuccess({ settings })),
          catchError((error) => {
            console.error('Error', error);
            return of(AlgoliaSearchActions.loadAlgoliaSearchSettingsFailure({ error }));
          })
        )
      )
    )
  );

  settingsPoll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlgoliaSearchActions.loadAlgoliaSearchSettingsSuccess),
      // retrieve expiration duration that is set on the feature flag
      withLatestFrom(this.featureFlagsFacade.featureValueAsObservable<number>(FeatureFlag.AlgoliaKeyDuration)),
      // 0 indicates infinite so don't re-request settings in that case
      filter(([action, duration]) => duration > 0),
      // convert duration from minutes to milliseconds
      map(([action, duration]) => duration * 1000),
      switchMap((duration) =>
        of(null).pipe(
          delay(duration),
          map(() => AlgoliaSearchActions.loadAlgoliaSearchSettings())
        )
      )
    )
  );
}
